/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuthStore } from '@store/auth.store';
import { msalRedirectPromise } from '@/lib/msal';
import type { User } from '@lms-types/index';

// Raw `fetch` on purpose: this exchange is what produces the bearer the `api`
// singleton attaches to everything else. There is no token yet, and a failure
// here must not run the singleton's 401 handling (clear auth, bounce to /login)
// while the user is mid sign-in.
async function exchangeAzureToken(idToken: string): Promise<{ user: User; token: string }> {
  const res = await fetch('/api/auth/azure/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  });
  if (!res.ok) throw new Error(`Token exchange failed: ${res.status}`);
  return JSON.parse(await res.text());
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function handleRedirect() {
      try {
        // Await the module-level promise — MSAL v3 processes the redirect
        // inside initialize(), so handleRedirectPromise() called here would
        // return null. msalRedirectPromise captures the result correctly.
        const result = await msalRedirectPromise;

        // idToken may be on result.idToken or inside result.account.idToken
        const idToken = result?.idToken ?? (result?.account as any)?.idToken;

        if (idToken) {
          const { user, token } = await exchangeAzureToken(idToken);
          if (!cancelled) {
            setAuth(user, token);
            navigate(user.role === 'learner' ? '/learn/dashboard' : '/dashboard', {
              replace: true,
            });
          }
          return;
        }

        // No redirect result — user navigated here directly
        if (!cancelled) navigate('/login', { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError('Sign in failed. Please try again.');
          setTimeout(() => navigate('/login', { replace: true }), 2000);
        }
      }
    }

    handleRedirect();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-900 via-navy-800 to-navy-950 flex items-center justify-center">
      {error ? (
        <p className="text-white text-sm">{error}</p>
      ) : (
        <div className="flex flex-col items-center gap-3 text-white">
          <Loader2 size={20} className="animate-spin" />
          <span className="text-sm">Completing sign in...</span>
        </div>
      )}
    </div>
  );
}
